import { useState, useMemo, useEffect } from 'react'
import {
  Calendar,
  Clock,
  Video,
  User,
  Mail,
  Link as LinkIcon,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Briefcase,
  FileText,
  Sparkles,
  Globe,
} from 'lucide-react'
import {
  useInterviewTeamConfig,
  useMultiUserAvailableSlots,
  useSlotsForDate,
} from '@/lib/data/hooks/useMultiUserAvailability'
import {
  useCreateAnimatorApplication,
  fetchTeamMembers,
} from '@/lib/data/hooks/useAnimatorApplications'
import {
  AnimatorType,
  ANIMATOR_TYPES,
  ANIMATOR_TYPE_LABELS,
  CreateAnimatorApplicationInput,
} from '@/types/animatorApplication'
import type { TimeSlot } from '@/types/calendar'

// Mentatix styling - dark theme with yellow accent
const styles = {
  bg: '#0a0a0a',
  card: '#141414',
  cardHover: '#1a1a1a',
  border: '#262626',
  accent: '#facc15',
  accentHover: '#fbbf24',
  text: '#f5f5f5',
  textMuted: '#a3a3a3',
  textDim: '#737373',
}

// Common timezones for international applicants
const COMMON_TIMEZONES = [
  // Americas
  { value: 'America/New_York', label: 'Eastern Time (ET)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (CT)', region: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', region: 'Americas' },
  { value: 'America/Anchorage', label: 'Alaska Time', region: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City', region: 'Americas' },
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Central European', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow', region: 'Europe' },
  // Asia
  { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India (IST)', region: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', region: 'Asia' },
  // Pacific / Oceania
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', region: 'Pacific' },
  { value: 'Australia/Melbourne', label: 'Melbourne', region: 'Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)', region: 'Pacific' },
]

// Get timezone abbreviation for display
function getTimezoneAbbr(timezone: string, date: Date = new Date()): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
    const parts = formatter.formatToParts(date)
    const tzPart = parts.find(p => p.type === 'timeZoneName')
    return tzPart?.value || timezone
  } catch {
    return timezone
  }
}

// Get friendly timezone label
function getTimezoneLabel(timezone: string): string {
  const found = COMMON_TIMEZONES.find(tz => tz.value === timezone)
  if (found) return found.label
  // Fallback: format the IANA name nicely
  return timezone.replace(/_/g, ' ').replace(/\//g, ' / ')
}

// Small inline hex spinner for buttons
function HexSpinner() {
  return (
    <svg
      viewBox="0 0 80 92"
      className="w-4 h-4 animate-spin"
      style={{ filter: 'drop-shadow(0 0 4px rgba(0, 0, 0, 0.3))' }}
    >
      <path
        d="M40 2 L74 24 L74 68 L40 90 L6 68 L6 24 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="6"
        strokeDasharray="200"
        strokeDashoffset="50"
      />
    </svg>
  )
}

// Mentatix-styled hex loader
function MentatixHexLoader({ text }: { text?: string }) {
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="hex-loader-mentatix" style={{ width: 80, height: 92 }}>
        <svg viewBox="0 0 80 92" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
          <path
            className="hex-outer-m"
            d="M40 2 L74 24 L74 68 L40 90 L6 68 L6 24 Z"
          />
          <path
            className="hex-y-m"
            d="M40 28 L40 50 M40 50 L40 65 M40 50 L25 35 M40 50 L55 35"
          />
        </svg>
      </div>
      {text && (
        <p className="mt-4 text-sm tracking-wider uppercase" style={{ color: styles.textMuted }}>
          {text}<span className="loading-dots-m" />
        </p>
      )}
      <style>{`
        .hex-outer-m {
          fill: none;
          stroke: ${styles.accent};
          stroke-width: 2;
          stroke-dasharray: 300;
          stroke-dashoffset: 300;
          animation: draw-hex-m 1.5s ease-in-out infinite;
          filter: drop-shadow(0 0 8px rgba(250, 204, 21, 0.4));
        }
        .hex-y-m {
          fill: none;
          stroke: ${styles.accent};
          stroke-width: 2;
          stroke-linecap: round;
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: draw-y-m 1.5s ease-in-out infinite;
          animation-delay: 0.3s;
          filter: drop-shadow(0 0 6px rgba(250, 204, 21, 0.5));
        }
        .hex-loader-mentatix {
          animation: glow-pulse-m 1.5s ease-in-out infinite;
        }
        @keyframes draw-hex-m {
          0% { stroke-dashoffset: 300; opacity: 0.3; }
          50% { stroke-dashoffset: 0; opacity: 1; }
          100% { stroke-dashoffset: -300; opacity: 0.3; }
        }
        @keyframes draw-y-m {
          0% { stroke-dashoffset: 100; opacity: 0; }
          30% { opacity: 0; }
          50% { stroke-dashoffset: 0; opacity: 1; }
          70% { opacity: 1; }
          100% { stroke-dashoffset: -100; opacity: 0; }
        }
        @keyframes glow-pulse-m {
          0%, 100% { filter: drop-shadow(0 0 10px rgba(250, 204, 21, 0.2)); }
          50% { filter: drop-shadow(0 0 25px rgba(250, 204, 21, 0.6)) drop-shadow(0 0 50px rgba(250, 204, 21, 0.3)); }
        }
        .loading-dots-m::after {
          content: '';
          animation: dots-m 1.5s steps(4, end) infinite;
        }
        @keyframes dots-m {
          0% { content: ''; }
          25% { content: '.'; }
          50% { content: '..'; }
          75% { content: '...'; }
          100% { content: ''; }
        }
      `}</style>
    </div>
  )
}

type ApplicationStep = 'info' | 'types' | 'date' | 'time' | 'review' | 'confirmation'

const STEPS: ApplicationStep[] = ['info', 'types', 'date', 'time', 'review']
const STEP_LABELS: Record<ApplicationStep, string> = {
  info: 'Your Info',
  types: 'Expertise',
  date: 'Date',
  time: 'Time',
  review: 'Review',
  confirmation: 'Done',
}

export function AnimatorApplicationPage() {
  // Form state
  const [step, setStep] = useState<ApplicationStep>('info')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [portfolioLinks, setPortfolioLinks] = useState<string[]>([''])
  const [about, setAbout] = useState('')
  const [animatorTypes, setAnimatorTypes] = useState<AnimatorType[]>([])
  const [strongAreas, setStrongAreas] = useState('')
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null)
  const [selectedTimezone, setSelectedTimezone] = useState(() =>
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )

  // Team members state
  const [teamMembers, setTeamMembers] = useState<{ id: string; email: string; firstName?: string; lastName?: string }[]>([])

  // Fetch interview team config
  const { config, isLoading: configLoading } = useInterviewTeamConfig('animator_interviews')

  // Calculate date range for availability (next 30 days)
  const today = new Date()
  const startDate = today.toISOString().split('T')[0]
  const endDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  // Fetch multi-user availability
  const {
    dayAvailability,
    isLoading: availabilityLoading,
  } = useMultiUserAvailableSlots({
    userIds: config?.teamMemberIds || [],
    startDate,
    endDate,
    duration: config?.interviewDuration || 30,
  })

  // Get slots for selected date
  const selectedDateSlots = useSlotsForDate(dayAvailability, selectedDate)

  // Create application
  const { createApplication, isLoading: submitting, error: submitError } = useCreateAnimatorApplication()
  const [confirmedApplication, setConfirmedApplication] = useState<{
    start: string
    end: string
    meetingLink?: string
  } | null>(null)

  // Fetch team member details when config loads
  useEffect(() => {
    if (config?.teamMemberIds?.length) {
      fetchTeamMembers(config.teamMemberIds).then(setTeamMembers)
    }
  }, [config])

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(today)

  // Calendar grid
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDay = firstDay.getDay()

    const days: (Date | null)[] = []
    for (let i = 0; i < startingDay; i++) days.push(null)
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i))
    return days
  }, [currentMonth])

  // Check if date has available slots
  const isDateAvailable = (date: Date) => {
    // Use local date format to match dayAvailability data
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    const dateStr = `${y}-${m}-${d}`

    const dayData = dayAvailability.find(day => day.date === dateStr)
    if (!dayData) return false
    return dayData.isAvailable
  }

  // Format time in selected timezone
  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: selectedTimezone,
    })
  }

  // Format date in selected timezone
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: selectedTimezone,
    })
  }

  // Get current timezone abbreviation
  const timezoneAbbr = getTimezoneAbbr(selectedTimezone)

  // Portfolio link handlers
  const addPortfolioLink = () => setPortfolioLinks([...portfolioLinks, ''])
  const removePortfolioLink = (index: number) => {
    if (portfolioLinks.length > 1) {
      setPortfolioLinks(portfolioLinks.filter((_, i) => i !== index))
    }
  }
  const updatePortfolioLink = (index: number, value: string) => {
    const updated = [...portfolioLinks]
    updated[index] = value
    setPortfolioLinks(updated)
  }

  // Animator type toggle
  const toggleAnimatorType = (type: AnimatorType) => {
    if (animatorTypes.includes(type)) {
      setAnimatorTypes(animatorTypes.filter(t => t !== type))
    } else {
      setAnimatorTypes([...animatorTypes, type])
    }
  }

  // Step validation
  const canProceed = () => {
    switch (step) {
      case 'info':
        return firstName.trim() && lastName.trim() && email.trim() && email.includes('@')
      case 'types':
        return animatorTypes.length > 0
      case 'date':
        return !!selectedDate
      case 'time':
        return !!selectedSlot
      case 'review':
        return true
      default:
        return false
    }
  }

  // Navigation
  const goNext = () => {
    const currentIndex = STEPS.indexOf(step)
    if (currentIndex < STEPS.length - 1) {
      setStep(STEPS[currentIndex + 1])
    }
  }

  const goBack = () => {
    const currentIndex = STEPS.indexOf(step)
    if (currentIndex > 0) {
      setStep(STEPS[currentIndex - 1])
    }
  }

  // Submit application
  const handleSubmit = async () => {
    if (!selectedSlot || !config) return

    const input: CreateAnimatorApplicationInput = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      portfolioLinks: portfolioLinks.filter(link => link.trim()),
      about: about.trim() || undefined,
      strongAreas: strongAreas.trim() || undefined,
      animatorTypes,
      scheduledStart: selectedSlot.start,
      scheduledEnd: selectedSlot.end,
      timezone: selectedTimezone,
    }

    const result = await createApplication(input, teamMembers)

    if (result) {
      setConfirmedApplication({
        start: selectedSlot.start,
        end: selectedSlot.end,
        meetingLink: result.meetingRoomLink,
      })
      setStep('confirmation')
    }
  }

  // Loading state
  if (configLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: styles.bg }}
      >
        <MentatixHexLoader text="Loading" />
      </div>
    )
  }

  // No config found
  if (!config) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: styles.bg }}
      >
        <div
          className="rounded-xl p-8 max-w-md w-full mx-4 text-center"
          style={{ backgroundColor: styles.card, border: `1px solid ${styles.border}` }}
        >
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#ef4444' }} />
          <h1 className="text-xl font-semibold mb-2" style={{ color: styles.text }}>
            Applications Not Available
          </h1>
          <p style={{ color: styles.textMuted }}>
            Interview scheduling is not currently configured. Please check back later.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen py-8 px-4"
      style={{ backgroundColor: styles.bg }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{ backgroundColor: `${styles.accent}15`, border: `1px solid ${styles.accent}30` }}
          >
            <Sparkles className="w-4 h-4" style={{ color: styles.accent }} />
            <span className="text-sm font-medium" style={{ color: styles.accent }}>
              Join Our Team
            </span>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: styles.text }}>
            Animator Application
          </h1>
          <p style={{ color: styles.textMuted }}>
            Apply to join Mentatix as an animator. Fill out the form and schedule your interview.
          </p>
        </div>

        {/* Progress Steps */}
        {step !== 'confirmation' && (
          <div className="flex items-center justify-center gap-1 mb-8">
            {STEPS.map((s, i) => {
              const isCurrent = step === s
              const isCompleted = STEPS.indexOf(step) > i

              return (
                <div key={s} className="flex items-center gap-1">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all"
                    style={{
                      backgroundColor: isCurrent
                        ? styles.accent
                        : isCompleted
                        ? '#22c55e'
                        : styles.card,
                      color: isCurrent || isCompleted ? '#000' : styles.textMuted,
                      border: isCurrent || isCompleted ? 'none' : `1px solid ${styles.border}`,
                    }}
                  >
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : i + 1}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className="w-8 h-0.5"
                      style={{
                        backgroundColor: STEPS.indexOf(step) > i ? '#22c55e' : styles.border,
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Step: Info */}
        {step === 'info' && (
          <div
            className="rounded-xl p-6"
            style={{ backgroundColor: styles.card, border: `1px solid ${styles.border}` }}
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: styles.text }}>
              <User className="w-5 h-5" style={{ color: styles.accent }} />
              Your Information
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: styles.text }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="John"
                    className="w-full px-4 py-2.5 rounded-lg transition-colors focus:outline-none"
                    style={{
                      backgroundColor: styles.bg,
                      border: `1px solid ${styles.border}`,
                      color: styles.text,
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: styles.text }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder="Doe"
                    className="w-full px-4 py-2.5 rounded-lg transition-colors focus:outline-none"
                    style={{
                      backgroundColor: styles.bg,
                      border: `1px solid ${styles.border}`,
                      color: styles.text,
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5 flex items-center gap-1.5" style={{ color: styles.text }}>
                  <Mail className="w-4 h-4" />
                  Email *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full px-4 py-2.5 rounded-lg transition-colors focus:outline-none"
                  style={{
                    backgroundColor: styles.bg,
                    border: `1px solid ${styles.border}`,
                    color: styles.text,
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5 flex items-center gap-1.5" style={{ color: styles.text }}>
                  <LinkIcon className="w-4 h-4" />
                  Portfolio Links
                </label>
                {portfolioLinks.map((link, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input
                      type="url"
                      value={link}
                      onChange={e => updatePortfolioLink(i, e.target.value)}
                      placeholder="https://your-portfolio.com"
                      className="flex-1 px-4 py-2.5 rounded-lg transition-colors focus:outline-none"
                      style={{
                        backgroundColor: styles.bg,
                        border: `1px solid ${styles.border}`,
                        color: styles.text,
                      }}
                    />
                    {portfolioLinks.length > 1 && (
                      <button
                        onClick={() => removePortfolioLink(i)}
                        className="p-2.5 rounded-lg transition-colors hover:opacity-80"
                        style={{ backgroundColor: styles.bg, border: `1px solid ${styles.border}` }}
                      >
                        <X className="w-4 h-4" style={{ color: styles.textMuted }} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addPortfolioLink}
                  className="flex items-center gap-1.5 text-sm transition-colors hover:opacity-80"
                  style={{ color: styles.accent }}
                >
                  <Plus className="w-4 h-4" />
                  Add another link
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5 flex items-center gap-1.5" style={{ color: styles.text }}>
                  <FileText className="w-4 h-4" />
                  About You
                </label>
                <textarea
                  value={about}
                  onChange={e => setAbout(e.target.value)}
                  placeholder="Tell us a bit about yourself, your experience, and what you're passionate about..."
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg transition-colors focus:outline-none resize-none"
                  style={{
                    backgroundColor: styles.bg,
                    border: `1px solid ${styles.border}`,
                    color: styles.text,
                  }}
                />
              </div>
            </div>

            <button
              onClick={goNext}
              disabled={!canProceed()}
              className="w-full mt-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: canProceed() ? styles.accent : styles.border,
                color: canProceed() ? '#000' : styles.textMuted,
              }}
            >
              Continue
            </button>
          </div>
        )}

        {/* Step: Types */}
        {step === 'types' && (
          <div
            className="rounded-xl p-6"
            style={{ backgroundColor: styles.card, border: `1px solid ${styles.border}` }}
          >
            <button
              onClick={goBack}
              className="flex items-center gap-1 text-sm mb-4 hover:opacity-80 transition-opacity"
              style={{ color: styles.textMuted }}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: styles.text }}>
              <Briefcase className="w-5 h-5" style={{ color: styles.accent }} />
              Your Expertise
            </h2>

            <p className="text-sm mb-4" style={{ color: styles.textMuted }}>
              Select all areas that apply to you *
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
              {ANIMATOR_TYPES.map(type => {
                const isSelected = animatorTypes.includes(type)
                return (
                  <button
                    key={type}
                    onClick={() => toggleAnimatorType(type)}
                    className="px-4 py-3 rounded-lg text-sm font-medium transition-all text-left"
                    style={{
                      backgroundColor: isSelected ? `${styles.accent}20` : styles.bg,
                      border: `1px solid ${isSelected ? styles.accent : styles.border}`,
                      color: isSelected ? styles.accent : styles.text,
                    }}
                  >
                    {ANIMATOR_TYPE_LABELS[type]}
                  </button>
                )
              })}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: styles.text }}>
                Strong Areas
              </label>
              <textarea
                value={strongAreas}
                onChange={e => setStrongAreas(e.target.value)}
                placeholder="What specific skills or styles are you strongest in? (e.g., character animation, kinetic typography, cel shading...)"
                rows={3}
                className="w-full px-4 py-2.5 rounded-lg transition-colors focus:outline-none resize-none"
                style={{
                  backgroundColor: styles.bg,
                  border: `1px solid ${styles.border}`,
                  color: styles.text,
                }}
              />
            </div>

            <button
              onClick={goNext}
              disabled={!canProceed()}
              className="w-full mt-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: canProceed() ? styles.accent : styles.border,
                color: canProceed() ? '#000' : styles.textMuted,
              }}
            >
              Continue
            </button>
          </div>
        )}

        {/* Step: Date */}
        {step === 'date' && (
          <div
            className="rounded-xl p-6"
            style={{ backgroundColor: styles.card, border: `1px solid ${styles.border}` }}
          >
            <button
              onClick={goBack}
              className="flex items-center gap-1 text-sm mb-4 hover:opacity-80 transition-opacity"
              style={{ color: styles.textMuted }}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: styles.text }}>
              <Calendar className="w-5 h-5" style={{ color: styles.accent }} />
              Select Interview Date
            </h2>

            <p className="text-sm mb-4" style={{ color: styles.textMuted }}>
              {config.interviewDuration} minute interview with the team
            </p>

            {/* Timezone Selector */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4" style={{ color: styles.accent }} />
                <span className="text-sm font-medium" style={{ color: styles.text }}>
                  Your Timezone
                </span>
              </div>
              <select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg transition-colors focus:outline-none text-sm"
                style={{
                  backgroundColor: styles.bg,
                  border: `1px solid ${styles.border}`,
                  color: styles.text,
                }}
              >
                {/* Current timezone if not in list */}
                {!COMMON_TIMEZONES.find(tz => tz.value === selectedTimezone) && (
                  <option value={selectedTimezone}>
                    {getTimezoneLabel(selectedTimezone)} ({timezoneAbbr})
                  </option>
                )}
                <optgroup label="Americas">
                  {COMMON_TIMEZONES.filter(tz => tz.region === 'Americas').map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({getTimezoneAbbr(tz.value)})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Europe">
                  {COMMON_TIMEZONES.filter(tz => tz.region === 'Europe').map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({getTimezoneAbbr(tz.value)})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Asia">
                  {COMMON_TIMEZONES.filter(tz => tz.region === 'Asia').map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({getTimezoneAbbr(tz.value)})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Pacific / Oceania">
                  {COMMON_TIMEZONES.filter(tz => tz.region === 'Pacific').map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({getTimezoneAbbr(tz.value)})
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* Calendar Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() =>
                  setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))
                }
                disabled={
                  currentMonth.getMonth() === today.getMonth() &&
                  currentMonth.getFullYear() === today.getFullYear()
                }
                className="p-2 rounded-lg transition-colors hover:opacity-80 disabled:opacity-30"
                style={{ backgroundColor: styles.bg }}
              >
                <ChevronLeft className="w-5 h-5" style={{ color: styles.textMuted }} />
              </button>
              <span className="font-medium" style={{ color: styles.text }}>
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <button
                onClick={() =>
                  setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))
                }
                className="p-2 rounded-lg transition-colors hover:opacity-80"
                style={{ backgroundColor: styles.bg }}
              >
                <ChevronRight className="w-5 h-5" style={{ color: styles.textMuted }} />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm py-2" style={{ color: styles.textDim }}>
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, i) => {
                if (!day) return <div key={`empty-${i}`} className="p-2" />

                // Use local date format consistently
                const y = day.getFullYear()
                const m = String(day.getMonth() + 1).padStart(2, '0')
                const d = String(day.getDate()).padStart(2, '0')
                const dateStr = `${y}-${m}-${d}`

                const todayY = today.getFullYear()
                const todayM = String(today.getMonth() + 1).padStart(2, '0')
                const todayD = String(today.getDate()).padStart(2, '0')
                const todayStr = `${todayY}-${todayM}-${todayD}`

                const isToday = dateStr === todayStr
                const isSelected = dateStr === selectedDate
                const isPast = day < new Date(today.getFullYear(), today.getMonth(), today.getDate())
                const isAvailable = !isPast && isDateAvailable(day)

                return (
                  <button
                    key={dateStr}
                    onClick={() => {
                      if (isAvailable) {
                        setSelectedDate(dateStr)
                        setSelectedSlot(null)
                        goNext()
                      }
                    }}
                    disabled={!isAvailable}
                    className="p-2 rounded-lg text-sm transition-all relative"
                    style={{
                      backgroundColor: isSelected ? styles.accent : 'transparent',
                      color: isSelected
                        ? '#000'
                        : isAvailable
                        ? styles.text
                        : styles.textDim,
                      opacity: !isAvailable ? 0.3 : 1,
                      cursor: isAvailable ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {day.getDate()}
                    {isToday && !isSelected && (
                      <div
                        className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                        style={{ backgroundColor: styles.accent }}
                      />
                    )}
                    {isAvailable && !isSelected && (
                      <div
                        className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: '#22c55e' }}
                      />
                    )}
                  </button>
                )
              })}
            </div>

            {availabilityLoading && (
              <div className="flex items-center justify-center py-8">
                <MentatixHexLoader text="Loading availability" />
              </div>
            )}
          </div>
        )}

        {/* Step: Time */}
        {step === 'time' && selectedDate && (
          <div
            className="rounded-xl p-6"
            style={{ backgroundColor: styles.card, border: `1px solid ${styles.border}` }}
          >
            <button
              onClick={goBack}
              className="flex items-center gap-1 text-sm mb-4 hover:opacity-80 transition-opacity"
              style={{ color: styles.textMuted }}
            >
              <ChevronLeft className="w-4 h-4" />
              Back to calendar
            </button>

            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2" style={{ color: styles.text }}>
              <Clock className="w-5 h-5" style={{ color: styles.accent }} />
              Select a Time
            </h2>
            <p className="mb-1" style={{ color: styles.textMuted }}>{formatDate(selectedDate)}</p>
            <p className="text-sm mb-4 flex items-center gap-1.5" style={{ color: styles.textDim }}>
              <Globe className="w-3.5 h-3.5" />
              Times shown in {getTimezoneLabel(selectedTimezone)} ({timezoneAbbr})
            </p>

            {selectedDateSlots.length === 0 ? (
              <p className="text-center py-8" style={{ color: styles.textMuted }}>
                No available times on this date. Please select another date.
              </p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {selectedDateSlots.map(slot => {
                  const isSelected = selectedSlot?.start === slot.start
                  return (
                    <button
                      key={slot.start}
                      onClick={() => {
                        setSelectedSlot(slot)
                        goNext()
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                      style={{
                        backgroundColor: isSelected ? styles.accent : styles.bg,
                        border: `1px solid ${isSelected ? styles.accent : styles.border}`,
                        color: isSelected ? '#000' : styles.text,
                      }}
                    >
                      {formatTime(slot.start)}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Step: Review */}
        {step === 'review' && selectedSlot && (
          <div
            className="rounded-xl p-6"
            style={{ backgroundColor: styles.card, border: `1px solid ${styles.border}` }}
          >
            <button
              onClick={goBack}
              className="flex items-center gap-1 text-sm mb-4 hover:opacity-80 transition-opacity"
              style={{ color: styles.textMuted }}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: styles.text }}>
              <CheckCircle className="w-5 h-5" style={{ color: styles.accent }} />
              Review Your Application
            </h2>

            <div className="space-y-4">
              {/* Personal Info */}
              <div className="rounded-lg p-4" style={{ backgroundColor: styles.bg }}>
                <h3 className="text-sm font-medium mb-2" style={{ color: styles.textMuted }}>
                  Personal Information
                </h3>
                <p style={{ color: styles.text }}>{firstName} {lastName}</p>
                <p style={{ color: styles.textMuted }}>{email}</p>
              </div>

              {/* Portfolio */}
              {portfolioLinks.some(link => link.trim()) && (
                <div className="rounded-lg p-4" style={{ backgroundColor: styles.bg }}>
                  <h3 className="text-sm font-medium mb-2" style={{ color: styles.textMuted }}>
                    Portfolio
                  </h3>
                  {portfolioLinks.filter(link => link.trim()).map((link, i) => (
                    <a
                      key={i}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm hover:opacity-80"
                      style={{ color: styles.accent }}
                    >
                      {link}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ))}
                </div>
              )}

              {/* Expertise */}
              <div className="rounded-lg p-4" style={{ backgroundColor: styles.bg }}>
                <h3 className="text-sm font-medium mb-2" style={{ color: styles.textMuted }}>
                  Expertise
                </h3>
                <div className="flex flex-wrap gap-2">
                  {animatorTypes.map(type => (
                    <span
                      key={type}
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: `${styles.accent}20`, color: styles.accent }}
                    >
                      {ANIMATOR_TYPE_LABELS[type]}
                    </span>
                  ))}
                </div>
                {strongAreas && (
                  <p className="mt-2 text-sm" style={{ color: styles.text }}>{strongAreas}</p>
                )}
              </div>

              {/* Interview Time */}
              <div className="rounded-lg p-4" style={{ backgroundColor: styles.bg }}>
                <h3 className="text-sm font-medium mb-2" style={{ color: styles.textMuted }}>
                  Interview
                </h3>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5" style={{ color: styles.accent }} />
                  <div>
                    <p style={{ color: styles.text }}>{formatDate(selectedDate!)}</p>
                    <p style={{ color: styles.textMuted }}>
                      {formatTime(selectedSlot.start)} - {formatTime(selectedSlot.end)} ({timezoneAbbr})
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <Video className="w-5 h-5" style={{ color: styles.accent }} />
                  <span style={{ color: styles.textMuted }}>Video call - link will be provided</span>
                </div>
              </div>
            </div>

            {submitError && (
              <div
                className="flex items-center gap-2 text-sm mt-4"
                style={{ color: '#ef4444' }}
              >
                <AlertCircle className="w-4 h-4" />
                {submitError}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full mt-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                backgroundColor: styles.accent,
                color: '#000',
              }}
            >
              {submitting ? (
                <>
                  <HexSpinner />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </div>
        )}

        {/* Step: Confirmation */}
        {step === 'confirmation' && confirmedApplication && (
          <div
            className="rounded-xl p-8 text-center"
            style={{ backgroundColor: styles.card, border: `1px solid ${styles.border}` }}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: '#22c55e20' }}
            >
              <CheckCircle className="w-8 h-8" style={{ color: '#22c55e' }} />
            </div>

            <h2 className="text-2xl font-bold mb-2" style={{ color: styles.text }}>
              Application Submitted!
            </h2>
            <p className="mb-6" style={{ color: styles.textMuted }}>
              We've received your application and scheduled your interview.
            </p>

            <div className="rounded-lg p-4 text-left mb-6" style={{ backgroundColor: styles.bg }}>
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="w-5 h-5" style={{ color: styles.accent }} />
                <div>
                  <p className="font-medium" style={{ color: styles.text }}>
                    {formatDate(selectedDate!)}
                  </p>
                  <p style={{ color: styles.textMuted }}>
                    {formatTime(confirmedApplication.start)} - {formatTime(confirmedApplication.end)} ({timezoneAbbr})
                  </p>
                </div>
              </div>

              {confirmedApplication.meetingLink && (
                <div className="flex items-center gap-3">
                  <Video className="w-5 h-5" style={{ color: styles.accent }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium mb-1" style={{ color: styles.text }}>
                      Meeting Link
                    </p>
                    <a
                      href={confirmedApplication.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm flex items-center gap-1 truncate hover:opacity-80"
                      style={{ color: styles.accent }}
                    >
                      {confirmedApplication.meetingLink}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            <p className="text-sm" style={{ color: styles.textMuted }}>
              A confirmation has been sent to <strong style={{ color: styles.text }}>{email}</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default AnimatorApplicationPage
